import { z } from "zod";
import {
  Box,
  WidgetEmpty,
  WidgetEmptyChoiceRow,
  toastManager,
  type IconName,
  type WidgetModule,
} from "@salestrak/ui";

/**
 * `agenda` — the My Work calendar card, in its connect state. No calendar
 * integration exists yet, so the card is a PLACEHOLDER in the kit's own
 * empty-state grammar: one line of copy and one choice
 * tile per provider (`WidgetEmptyChoiceRow` — name on the left, Connect on
 * the right). A press says so in a toast rather than doing nothing — a dead
 * button reads as a bug. When an integration
 * lands, this render grows the connected state (upcoming appointments) and
 * the rows become the real connect flow. Managed-only: no catalog entry,
 * seeded by `MY_WORK_BOARD`.
 */
export const agendaConfigSchema = z.looseObject({});

export type AgendaConfig = z.infer<typeof agendaConfigSchema>;

/** The providers the connect state offers, each leading with its own mark
 *  from `@salestrak/icons` (manual brand icons beside the Google sign-in mark). */
const PROVIDERS: readonly { id: string; name: string; icon: IconName }[] = [
  { id: "google", name: "Google Calendar", icon: "google-calendar" },
  { id: "outlook", name: "Microsoft Outlook", icon: "microsoft-outlook" },
];

function connectPlaceholder(label: string) {
  toastManager.add({
    title: `${label} coming soon`,
    description: "Calendar connections can't be set up yet.",
    type: "info",
  });
}

function AgendaRender() {
  return (
    <WidgetEmpty
      title="Connect your calendar"
      action={
        <Box direction="column" gap="2xs" width="100%" maxWidth={400}>
          {PROVIDERS.map((provider) => (
            <WidgetEmptyChoiceRow
              key={provider.id}
              label={provider.name}
              actionLabel="Connect"
              onAction={() => connectPlaceholder(provider.name)}
            />
          ))}
        </Box>
      }
    />
  );
}

export const agendaDef: WidgetModule<AgendaConfig> = {
  type: "agenda",
  title: "Agenda",
  icon: "calendar",
  // The card holds no projects — the host's default dataset would be
  // unrelated, so the Data tab hides.
  drilldownDataset: null,
  span: { minW: 4, minH: 3, maxW: 12, maxH: 12, defaultW: 6 },
  configSchema: agendaConfigSchema,
  settings: [],
  render: AgendaRender,
};
